import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';

import {
  SelectedTimeReportTypeEnum,
  TimeReportSessionTypeEnum
} from '@app/shared/types/models/reports/time-report-panel.model';
import { resolveProffeoChartColor } from '@app/shared/ui/tokens/chart.tokens';
import moment from 'moment';
import { HOURS_TYPE_COLORS, HOURS_TYPE_LABELS } from '../constants/chart-scale.constants';
import { ChartDataModel, ChartDatasetModel } from '../models/chart.models';
import { TimeReportCalculatorService } from './time-report-calculator.service';
import { WORK_TIME_CHART_SERIES } from './work-time-chart-durations';

@Injectable()
export class ChartDataFormatterService {
  private readonly datePipe = inject(DatePipe);
  private readonly calculatorService = inject(TimeReportCalculatorService);

  public getChartDataByMode(
    mode: SelectedTimeReportTypeEnum,
    filteredReports: WorkTimeReport[][],
    selectedYear: number,
    selectedMonth: number,
    selectedWeek: number,
    selectedDay: number
  ): ChartDataModel {
    const labels = this.getLabelsForMode(mode, selectedYear, selectedMonth, selectedWeek, selectedDay);
    const dates = this.getDatesForMode(mode, selectedYear, selectedMonth, selectedWeek, selectedDay);
    const dateComparison: 'day' | 'month' = mode === SelectedTimeReportTypeEnum.YEAR ? 'month' : 'day';

    const datasets = this.createDatasets(filteredReports, dates, dateComparison);

    return { labels, datasets };
  }

  public getRangeChartData(filteredReports: WorkTimeReport[][], userCount: number): ChartDataModel {
    return {
      labels: [
        HOURS_TYPE_LABELS.TOTAL,
        HOURS_TYPE_LABELS.STANDARD,
        HOURS_TYPE_LABELS.BANK_PLUS,
        HOURS_TYPE_LABELS.BANK_MINUS,
        HOURS_TYPE_LABELS.OVERTIME_50,
        HOURS_TYPE_LABELS.OVERTIME_100
      ],
      datasets: [
        {
          label: HOURS_TYPE_LABELS.BASE_HOURS,
          data: [
            this.sumAndParse(filteredReports),
            this.sumAndParse(filteredReports, TimeReportSessionTypeEnum.STANDARD),
            this.sumAndParse(filteredReports, TimeReportSessionTypeEnum.BANK_PLUS),
            this.sumAndParse(filteredReports, TimeReportSessionTypeEnum.BANK_MINUS),
            this.sumAndParse(filteredReports, TimeReportSessionTypeEnum.OVERTIME_50),
            this.sumAndParse(filteredReports, TimeReportSessionTypeEnum.OVERTIME_100)
          ],
          backgroundColor: HOURS_TYPE_COLORS.BASE,
          borderWidth: 1,
          stack: 'work'
        },
        {
          label: HOURS_TYPE_LABELS.OVERTIME,
          data: [
            this.calculateOvertimeData(filteredReports, userCount),
            this.calculateOvertimeData(filteredReports, userCount, TimeReportSessionTypeEnum.STANDARD),
            this.calculateOvertimeData(filteredReports, userCount, TimeReportSessionTypeEnum.BANK_PLUS),
            this.calculateOvertimeData(filteredReports, userCount, TimeReportSessionTypeEnum.BANK_MINUS),
            this.calculateOvertimeData(filteredReports, userCount, TimeReportSessionTypeEnum.OVERTIME_50),
            this.calculateOvertimeData(filteredReports, userCount, TimeReportSessionTypeEnum.OVERTIME_100)
          ],
          backgroundColor: HOURS_TYPE_COLORS.OVERTIME,
          borderWidth: 1,
          stack: 'work'
        }
      ]
    };
  }

  private createDatasets(
    filteredReports: WorkTimeReport[][],
    dates: Date[],
    dateComparison: 'day' | 'month'
  ): ChartDatasetModel[] {
    const datasets: ChartDatasetModel[] = [];

    WORK_TIME_CHART_SERIES.forEach(type => {
      datasets.push(this.createDatasetForHoursType(filteredReports, dates, type, dateComparison));
    });

    return datasets;
  }

  private createDatasetForHoursType(
    filteredReports: WorkTimeReport[][],
    dates: Date[],
    hoursType: TimeReportSessionTypeEnum,
    dateComparison: 'day' | 'month'
  ): ChartDatasetModel {
    const config = this.getHoursTypeConfig(hoursType);
    const data = this.calculatorService.sumTimeForDatesAndHoursType(filteredReports, dates, hoursType, dateComparison);

    return {
      label: config.label,
      data,
      backgroundColor: config.color,
      barPercentage: 0.8,
      categoryPercentage: 0.8
    };
  }

  private getHoursTypeConfig(type: TimeReportSessionTypeEnum): { label: string; color: string } {
    switch (type) {
      case TimeReportSessionTypeEnum.STANDARD:
        return { label: HOURS_TYPE_LABELS.STANDARD, color: resolveProffeoChartColor(HOURS_TYPE_COLORS.STANDARD) };
      case TimeReportSessionTypeEnum.BANK_PLUS:
        return { label: HOURS_TYPE_LABELS.BANK_PLUS, color: resolveProffeoChartColor(HOURS_TYPE_COLORS.BANK_PLUS) };
      case TimeReportSessionTypeEnum.OVERTIME_50:
        return {
          label: HOURS_TYPE_LABELS.OVERTIME_50,
          color: resolveProffeoChartColor(HOURS_TYPE_COLORS.OVERTIME_50)
        };
      case TimeReportSessionTypeEnum.OVERTIME_100:
        return {
          label: HOURS_TYPE_LABELS.OVERTIME_100,
          color: resolveProffeoChartColor(HOURS_TYPE_COLORS.OVERTIME_100)
        };
      default:
        return { label: 'Unknown', color: '#999' };
    }
  }

  private getLabelsForMode(
    mode: SelectedTimeReportTypeEnum,
    selectedYear: number,
    selectedMonth: number,
    selectedWeek: number,
    selectedDay: number
  ): string[] {
    moment.locale('en', { week: { dow: 1 } });

    switch (mode) {
      case SelectedTimeReportTypeEnum.DAY:
        return [
          this.datePipe.transform(
            moment({ year: selectedYear, month: selectedMonth, day: selectedDay }).toDate(),
            'd MMM y'
          ) || ''
        ];

      case SelectedTimeReportTypeEnum.WEEK:
        return moment.weekdaysShort(true);

      case SelectedTimeReportTypeEnum.MONTH: {
        const daysInMonth = moment({ year: selectedYear, month: selectedMonth }).daysInMonth();
        return Array.from({ length: daysInMonth - 1 }, (_, i) => (i + 1).toString());
      }

      case SelectedTimeReportTypeEnum.YEAR:
        return moment.monthsShort();

      default:
        return [];
    }
  }

  private getDatesForMode(
    mode: SelectedTimeReportTypeEnum,
    selectedYear: number,
    selectedMonth: number,
    selectedWeek: number,
    selectedDay: number
  ): Date[] {
    const dates: Date[] = [];

    switch (mode) {
      case SelectedTimeReportTypeEnum.DAY:
        dates.push(moment({ year: selectedYear, month: selectedMonth, day: selectedDay }).toDate());
        break;

      case SelectedTimeReportTypeEnum.WEEK: {
        const startOfWeek = moment().year(selectedYear).week(selectedWeek).startOf('isoWeek');
        for (let i = 0; i < 7; i++) {
          dates.push(startOfWeek.clone().add(i, 'days').toDate());
        }
        break;
      }

      case SelectedTimeReportTypeEnum.MONTH: {
        const startOfMonth = moment({ year: selectedYear, month: selectedMonth }).startOf('month');
        const endOfMonth = moment({ year: selectedYear, month: selectedMonth }).endOf('month').date();
        for (let i = 1; i < endOfMonth; i++) {
          dates.push(startOfMonth.clone().add(i, 'days').toDate());
        }
        break;
      }

      case SelectedTimeReportTypeEnum.YEAR: {
        const startOfYear = moment({ year: selectedYear }).startOf('year');
        for (let i = 1; i < 12; i++) {
          dates.push(startOfYear.clone().add(i, 'months').toDate());
        }
        break;
      }
    }

    return dates;
  }

  private sumAndParse(filteredReports: WorkTimeReport[][], type?: TimeReportSessionTypeEnum): number {
    const summed = this.calculatorService.sumAllSessionsTime(filteredReports, type);
    return this.calculatorService.parseTimeToHours(summed);
  }

  private calculateOvertimeData(
    filteredReports: WorkTimeReport[][],
    userCount: number,
    type?: TimeReportSessionTypeEnum
  ): number {
    const summed = this.calculatorService.sumAllSessionsTime(filteredReports, type);
    return this.calculatorService.calculateOvertime(summed, userCount, 7.5);
  }
}
